/* Massage Booking — LIVE picker controller (.mbk). Vanilla JS, no jQuery.
   Real-time date/time picker for AAC massage (Kevin Runge).

   ── #648: THE RENDERING MOVED OUT ─────────────────────────────────────────────
   This file used to render the month grid and the time chips itself — renderMonth,
   paintAvailability, renderSlots, the roving-tabindex focus model, the grid and
   chip keydown handlers. All of that is now the SHARED picker
   (assets/component-sk-booking-picker.js), which the tutoring form renders too,
   so the next provider inherits the calendar instead of copying it.

   What stayed is everything that is actually about MASSAGE BOOKING, and it is
   unchanged:
     * the #mbk-config read (base URL, shop timezone)
     * the /availability + /slots calls and their response normalisers, which
       tolerate several response shapes
     * the DST-safe UTC-instant derivation (localWallToISO), which this form
       needs because it emits `_slot_start_iso`
     * all FIVE line-item properties, under their existing names
     * whole-widget fallback on a first-load failure
     * the capture-phase Add-to-Cart guard across every /cart/add form

   The split is deliberate: the shared picker knows no endpoint, no therapist, no
   duration and — above all — no property names. Property names are the contract
   with sk-massage-order-sync and are hardcoded string literals there, so they
   stay in the file that owns the booking, where an extraction cannot rename one
   by accident.

   TIMEZONE / ISO instant (no external libs):
     The server is the source of truth and recomputes ends_at; the browser only
     needs the correct UTC instant for the chosen wall-clock time in the shop tz.
     We derive the tz offset for a given instant via Intl.DateTimeFormat + a
     double-evaluation (guess UTC → measure offset → re-apply → re-measure), which
     is DST-safe: the second pass corrects any spring-forward/fall-back guess. */
(function () {
  'use strict';

  var PHONE = '(979) 277-7674';

  /* ── Config ── */
  function readConfig() {
    var el = document.getElementById('mbk-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ── Timezone helpers ── */
  // Offset (minutes) such that: localWallClock = UTC + offset. Chicago CST = -360, CDT = -300.
  function tzOffsetMinutes(instant, tz) {
    var dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
    var parts = {};
    dtf.formatToParts(instant).forEach(function (p) { parts[p.type] = p.value; });
    var h = parts.hour === '24' ? 0 : parseInt(parts.hour, 10);
    var asUTC = Date.UTC(
      parseInt(parts.year, 10), parseInt(parts.month, 10) - 1, parseInt(parts.day, 10),
      h, parseInt(parts.minute, 10), parseInt(parts.second, 10)
    );
    return (asUTC - instant.getTime()) / 60000;
  }

  // ISO instant for a wall-clock local time (y, mo 1-12, d, h, mi) in tz.
  function localWallToISO(y, mo, d, h, mi, tz) {
    var guess = Date.UTC(y, mo - 1, d, h, mi, 0);
    var off = tzOffsetMinutes(new Date(guess), tz);
    var ts = guess - off * 60000;
    off = tzOffsetMinutes(new Date(ts), tz); // second pass corrects DST edges
    ts = Date.UTC(y, mo - 1, d, h, mi, 0) - off * 60000;
    return new Date(ts).toISOString();
  }

  // Today's Y/M/D in tz (a tz-local calendar date, not an instant).
  function todayYmd(tz) {
    var dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit'
    });
    var parts = {};
    dtf.formatToParts(new Date()).forEach(function (p) { parts[p.type] = p.value; });
    return parts.year + '-' + parts.month + '-' + parts.day;
  }

  /* ── Date string helpers (calendar dates are tz-local 'YYYY-MM-DD', never Dates) ── */
  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function partsOf(ymdStr) {
    var p = String(ymdStr).split('-');
    return { y: parseInt(p[0], 10), m: parseInt(p[1], 10), d: parseInt(p[2], 10) };
  }
  function addDays(ymdStr, n) {
    var p = partsOf(ymdStr);
    var dt = new Date(Date.UTC(p.y, p.m - 1, p.d + n));
    return dt.getUTCFullYear() + '-' + pad2(dt.getUTCMonth() + 1) + '-' + pad2(dt.getUTCDate());
  }

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // "09:00" (24h) → "9:00 AM"
  function to12h(hhmm) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
    if (!m) return String(hhmm);
    var h = parseInt(m[1], 10), mi = m[2];
    var ampm = h >= 12 ? 'PM' : 'AM';
    var dh = h % 12; if (dh === 0) dh = 12;
    return dh + ':' + mi + ' ' + ampm;
  }

  // Human date label "Monday, July 13". Built from parts so it is parsed local, not UTC.
  function dateLabel(ymdStr) {
    var p = partsOf(ymdStr);
    var dt = new Date(p.y, p.m - 1, p.d);
    return DAY_NAMES[dt.getDay()] + ', ' + MONTHS[p.m - 1] + ' ' + p.d;
  }

  /* ── Fetch helpers ── */
  function apiGet(base, path, params) {
    var qs = Object.keys(params).map(function (k) {
      return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
    }).join('&');
    var url = base.replace(/\/+$/, '') + path + '?' + qs;
    return fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' }, mode: 'cors' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  // Normalize /availability response → { 'YYYY-MM-DD': count }
  function parseAvailability(data) {
    var arr = Array.isArray(data) ? data : (data && (data.days || data.availability || data.data)) || [];
    var map = {};
    (arr || []).forEach(function (row) {
      if (!row) return;
      var date = row.date || row.day || row.local_date;
      if (!date) return;
      var count = (row.count != null) ? row.count
        : (Array.isArray(row.slots) ? row.slots.length : (row.available ? 1 : 0));
      map[date] = count || 0;
    });
    return map;
  }

  // Normalize /slots response → array of "HH:MM" 24h strings
  function parseSlots(data) {
    var arr = Array.isArray(data) ? data : (data && (data.slots || data.times || data.data)) || [];
    return (arr || []).map(function (s) {
      if (typeof s === 'string') return s.trim();
      if (s && typeof s === 'object') return (s.time || s.start || s.value || '').trim();
      return '';
    }).filter(Boolean);
  }

  /* ── Controller ── */
  function initRoot(root, cfg) {
    if (root.getAttribute('data-mbk-init') === '1') return;
    root.setAttribute('data-mbk-init', '1');

    var base = cfg.baseUrl;
    var tz = cfg.timezone || 'America/Chicago';
    var therapist = root.getAttribute('data-therapist') || cfg.therapist || '';
    var duration = parseInt(root.getAttribute('data-duration'), 10) || parseInt(cfg.duration, 10) || 60;
    var horizonDays = 60;

    var el = {
      widget: root.querySelector('[data-mbk-widget]'),
      pickerRoot: root.querySelector('[data-skb-root]'),
      fallback: root.querySelector('[data-mbk-fallback]'),
      fallbackDate: root.querySelector('[data-mbk-fallback-date]'),
      error: root.querySelector('[data-mbk-error]'),
      propDate: root.querySelector('[data-mbk-prop="date"]'),
      propTime: root.querySelector('[data-mbk-prop="time"]'),
      propIso: root.querySelector('[data-mbk-prop="iso"]'),
      propTherapist: root.querySelector('[data-mbk-prop="therapist"]'),
      propDuration: root.querySelector('[data-mbk-prop="duration"]')
    };

    var today = todayYmd(tz);
    var maxDate = addDays(today, horizonDays);
    var mode = 'interactive';        // 'interactive' | 'fallback'
    var slotToken = 0;               // guards out-of-order slot fetches
    var hasLoaded = false;           // first successful availability load
    var availability = {};           // 'YYYY-MM-DD' → count, accumulated across months
    var picker = null;               // assigned when the shared asset has run
    var pendingCal = null;           // calendar message raised before the picker existed

    /* Enable the hidden property carriers now that JS is live; seed the machine fields. */
    [el.propDate, el.propTime, el.propIso, el.propTherapist, el.propDuration].forEach(function (i) {
      if (i) i.disabled = false;
    });
    setProp(el.propTherapist, therapist);
    setProp(el.propDuration, String(duration));

    function setProp(input, value) {
      if (!input) return;
      input.value = value == null ? '' : value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function showError(msg) {
      if (!el.error) return;
      if (msg) { el.error.textContent = msg; el.error.hidden = false; }
      else { el.error.hidden = true; el.error.textContent = ''; }
    }

    /* The picker constructor renders its first month synchronously, which fires onMonth before
       `picker` has been assigned. Park anything it wants to say and replay it. */
    function setCalMsg(text, kind) {
      if (picker) picker.setCalendarMessage(text, kind);
      else pendingCal = { text: text, kind: kind };
    }

    /* ── Buy-button gate ── */
    // EVERY /cart/add form on the page (main product form + any sticky/quick-view
    // form), so a booking can't be submitted through an unguarded form.
    function getForms() {
      var set = [];
      var main = document.querySelector('product-form form');
      if (main) set.push(main);
      document.querySelectorAll('form[action*="/cart/add"]').forEach(function (f) {
        if (set.indexOf(f) === -1) set.push(f);
      });
      return set;
    }
    function isValidSelection() {
      if (mode === 'fallback') return !!(el.propDate && el.propDate.value.trim());
      return !!(el.propIso && el.propIso.value.trim());
    }
    function syncBuyButton() {
      var ok = isValidSelection();
      getForms().forEach(function (form) {
        form.querySelectorAll('[name="add"], .product-form__submit, button[type="submit"]').forEach(function (b) {
          if (ok) { b.removeAttribute('aria-disabled'); b.classList.remove('mbk-blocked'); }
          else { b.setAttribute('aria-disabled', 'true'); b.classList.add('mbk-blocked'); }
        });
      });
    }
    function submitGuard(e) {
      if (isValidSelection()) { showError(''); return; }
      e.preventDefault();
      e.stopImmediatePropagation();
      showError(mode === 'fallback'
        ? 'Please choose your preferred date, then call ' + PHONE + ' to confirm your time.'
        : 'Please choose an appointment date and time before adding to cart.');
      if (mode === 'fallback' && el.fallbackDate) el.fallbackDate.focus();
      else if (picker) picker.focusGrid();
    }
    function wireGuard() {
      getForms().forEach(function (form) {
        if (form.getAttribute('data-mbk-guard') === '1') return;
        form.setAttribute('data-mbk-guard', '1');
        form.addEventListener('submit', submitGuard, true); // capture, before theme handlers
        form.querySelectorAll('[name="add"], .product-form__submit').forEach(function (b) {
          b.addEventListener('click', submitGuard, true);
        });
      });
    }

    /* ── Fallback mode ── */
    function enterFallback() {
      mode = 'fallback';
      if (el.widget) el.widget.hidden = true;
      if (el.fallback) el.fallback.hidden = false;
      // In fallback we cannot compute an ISO instant; clear the machine fields.
      setProp(el.propIso, '');
      setProp(el.propTime, '');
      if (el.fallbackDate) {
        el.fallbackDate.setAttribute('min', today);
        el.fallbackDate.setAttribute('max', maxDate);
        el.fallbackDate.addEventListener('change', function () {
          setProp(el.propDate, el.fallbackDate.value || '');
          showError('');
          syncBuyButton();
        });
      }
      wireGuard();
      syncBuyButton();
    }

    /* ── Selection ── */
    function clearSelectionProps() {
      setProp(el.propDate, '');
      setProp(el.propTime, '');
      setProp(el.propIso, '');
      if (picker) picker.setSummary('');
      syncBuyButton();
    }

    /* The chip VALUE is the server's own 24h "HH:MM"; the chip LABEL is the 12h form. Keeping
       both means the UTC instant is derived from the unambiguous string rather than re-parsed
       out of "3:30 PM". */
    function chooseSlot(value, ymdStr) {
      var hm = /^(\d{1,2}):(\d{2})$/.exec(value);
      var p = partsOf(ymdStr);
      var iso = hm ? localWallToISO(p.y, p.m, p.d, parseInt(hm[1], 10), parseInt(hm[2], 10), tz) : '';
      setProp(el.propDate, ymdStr);
      setProp(el.propTime, to12h(value));
      setProp(el.propIso, iso);
      if (picker) picker.setSummary(dateLabel(ymdStr) + ' at ' + to12h(value));
      showError('');
      syncBuyButton();
    }

    /* ── Slots ── */
    function loadSlots(ymdStr) {
      var myToken = ++slotToken;
      clearSelectionProps();
      picker.setSlotsMessage('Loading times…');

      apiGet(base, '/slots', { therapist: therapist, date: ymdStr, duration: duration })
        .then(function (data) {
          if (myToken !== slotToken) return; // superseded by a newer click
          var times = parseSlots(data);
          if (!times.length) {
            picker.setSlots([]);
            picker.setSlotsMessage('No open times on this date. Try another day.');
            return;
          }
          picker.setSlotsMessage('');
          picker.setSlots(times.map(function (t) {
            return { value: t, label: to12h(t) };
          }));
        })
        .catch(function () {
          if (myToken !== slotToken) return;
          picker.setSlots([]);
          picker.setSlotsMessage('Couldn’t load times for this date. Please try again, or call ' + PHONE + '.', 'error');
        });
    }

    /* ── Availability, one visible month at a time ── */
    function fetchMonthAvailability(fromYmd, toYmd) {
      setCalMsg('Loading availability…');
      apiGet(base, '/availability', { therapist: therapist, duration: duration, from: fromYmd, to: toYmd })
        .then(function (data) {
          var map = parseAvailability(data);
          Object.keys(map).forEach(function (k) { availability[k] = map[k]; });
          hasLoaded = true;
          setCalMsg('');
          if (picker) picker.setAvailability(availability, 'off');
        })
        .catch(function () {
          /* First-ever load failing = no usable widget → whole-widget fallback.
             A later month's fetch failing (the widget already worked) → soft calendar error. */
          if (!hasLoaded) { enterFallback(); return; }
          setCalMsg('Couldn’t load availability for this month. Try again, or call ' + PHONE + '.', 'error');
        });
    }

    /* ── Boot ── */
    if (!base || !therapist) { enterFallback(); return; }
    if (!el.pickerRoot) { enterFallback(); return; }

    if (el.widget) el.widget.hidden = false;

    window.SKBookingPicker = window.SKBookingPicker || {
      _queue: [], ready: function (fn) { this._queue.push(fn); }
    };
    window.SKBookingPicker.ready(function (SKB) {
      picker = SKB.create(el.pickerRoot, {
        today: today,
        min: today,
        max: maxDate,
        emptyPanelText: 'Select a date to see available times.',
        onMonth: fetchMonthAvailability,
        onDay: loadSlots,
        onSlot: chooseSlot,
        onClear: function () { clearSelectionProps(); }
      });
      picker.setAvailability(availability, 'off');
      if (pendingCal) { picker.setCalendarMessage(pendingCal.text, pendingCal.kind); pendingCal = null; }
      syncBuyButton();
    });

    wireGuard();
    syncBuyButton();
  }

  /* ── Entry point + re-init ── */
  function boot() {
    var cfg = readConfig();
    var roots = document.querySelectorAll('[data-mbk-root]');
    if (!roots.length) return;
    roots.forEach(function (root) {
      if (!cfg) {
        // No config → straight to fallback (widget stays hidden, fallback shown).
        var fb = root.querySelector('[data-mbk-fallback]');
        var wg = root.querySelector('[data-mbk-widget]');
        if (wg) wg.hidden = true;
        if (fb) fb.hidden = false;
        return;
      }
      initRoot(root, cfg);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor / dynamic section reloads.
  document.addEventListener('shopify:section:load', boot);
})();
